import { Injectable } from '@angular/core';
import { ColumnSettings } from '@rivethealth/data/saved-view';
import { SuperColumn } from '@rivethealth/data/super-table';
import { debounce } from 'lodash';
import { catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { ColumnPreferenceService } from '../../api/column-preference.service';
import { AuthService } from '../../common/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class TableColumnStore {
  customizeIndexesThrough?: number;

  constructor(
    private readonly auth: AuthService,
    private readonly columnPreferenceService: ColumnPreferenceService,
  ) {}

  private key(tableName: string): string {
    return `${tableName}_columns:${this.auth.getUserId()}`;
  }

  private findColumnWithName(
    settings: ColumnSettings[],
    columnName: string,
  ): ColumnSettings | undefined {
    return settings.find(setting => this.settingHasName(setting, columnName));
  }

  private findIndexWithName(
    settings: ColumnSettings[],
    columnName: string,
  ): number {
    return settings.findIndex(setting =>
      this.settingHasName(setting, columnName),
    );
  }

  private cache: Map<string, Promise<ColumnSettings[]>> = new Map();

  async get(tableName: string): Promise<ColumnSettings[]> {
    if (!tableName) return [];
    if (!this.cache.has(tableName)) {
      this.cache.set(
        tableName,
        firstValueFrom(
          this.columnPreferenceService.get(tableName).pipe(
            catchError(e => {
              if (e.status !== 404) throw e;
              // If we've gotten a 404, the user doesn't yet have any settings
              // stored in the DB. We'll check localStorage in that case, and
              // if they have settings there, we'll migrate them behind the
              // scenes. This code can be removed after we feel confident that
              // most of our active users have visited their favorite tables
              // and run this code. Ticket for this at RIVET-10705.
              const key = this.key(tableName);
              const raw = localStorage.getItem(key);
              if (raw) {
                const config = JSON.parse(raw);
                return this.columnPreferenceService.set(tableName, config).pipe(
                  tap(() => {
                    localStorage.removeItem(key);
                  }),
                  map(() => config),
                );
              } else {
                return of([]);
              }
            }),
          ),
        ),
      );
    }
    return await this.cache.get(tableName)!;
  }

  async getWidth(
    tableName: string,
    columnName: string,
  ): Promise<number | null> {
    if (!tableName || !columnName) {
      return null;
    }
    const currentSettings = await this.get(tableName);
    const setting = this.findColumnWithName(currentSettings, columnName);
    return setting ? setting.width : null;
  }

  async moveColumnToPosition(
    tableName: string,
    index: number,
    finalPosition: number,
  ) {
    if (!tableName) {
      return;
    }
    const currentSettingsCopy = [...(await this.get(tableName))];
    if (finalPosition < 0 || finalPosition >= currentSettingsCopy.length) {
      return;
    }
    currentSettingsCopy[index].pinned =
      currentSettingsCopy[finalPosition].pinned;

    await this.set(tableName, currentSettingsCopy);

    currentSettingsCopy.splice(
      finalPosition,
      0,
      currentSettingsCopy.splice(index, 1)[0],
    );

    await this.setOrder(
      tableName,
      currentSettingsCopy.map(setting => setting.name),
    );
  }

  private async orderByPinned(tableName: string, settings: ColumnSettings[]) {
    if (!tableName) {
      return;
    }

    const settingNamesInPinnedOrder: string[] = settings
      .filter(col => col.pinned)
      .concat(settings.filter(col => !col.pinned))
      .map(col => col.name);

    await this.setOrder(tableName, settingNamesInPinnedOrder);
  }

  private filterToUniqueColumns(settings: ColumnSettings[]) {
    const uniqueSettings: ColumnSettings[] = [];
    settings.forEach(setting => {
      if (
        !uniqueSettings.some(uniqueSetting =>
          this.settingHasName(uniqueSetting, setting.name),
        )
      ) {
        uniqueSettings.push(setting);
      }
    });
    return uniqueSettings;
  }

  private persist = debounce(
    (tableKey: string, config: ColumnSettings[]) =>
      firstValueFrom(this.columnPreferenceService.set(tableKey, config)),
    500,
    { trailing: true },
  );

  async set(tableName: string, settings: ColumnSettings[]) {
    if (!tableName) {
      return;
    }
    if (
      this.customizeIndexesThrough &&
      settings.length > this.customizeIndexesThrough
    ) {
      settings.splice(0, this.customizeIndexesThrough);
    }
    const finalSettings: ColumnSettings[] =
      this.filterToUniqueColumns(settings);
    this.cache.set(tableName, Promise.resolve(finalSettings));
    await this.persist(tableName, settings);
  }

  setCustomizeIndexesThrough(customizeIndexesThrough: number) {
    this.customizeIndexesThrough = customizeIndexesThrough;
  }

  async setEnabled(
    tableName: string,
    selectedColumns: string[],
  ): Promise<void> {
    if (!tableName) {
      return;
    }
    const currentSettings = await this.get(tableName);

    currentSettings.forEach(setting => {
      setting.enabled = this.settingNameInList(setting, selectedColumns);
    });

    selectedColumns.forEach(selectedColumnName => {
      if (!this.findColumnWithName(currentSettings, selectedColumnName)) {
        currentSettings.push({
          name: selectedColumnName.toLowerCase(),
          enabled: true,
          width: null,
          minWidth: null,
          ordinal: currentSettings.length,
          pinned: false,
        });
      }
    });

    await this.set(tableName, currentSettings);
  }

  async setOrder(
    tableName: string,
    columnNamesInOrder: string[],
  ): Promise<void> {
    if (!tableName) {
      return;
    }
    const settings = await this.get(tableName);

    await this.set(
      tableName,
      columnNamesInOrder.map((colName, index) => {
        const match = this.findColumnWithName(settings, colName);
        return {
          name: match?.name.toLowerCase() || '',
          enabled: match?.enabled || false,
          width: match?.width || null,
          minWidth: match?.minWidth || null,
          ordinal: index,
          pinned: match?.pinned || false,
        };
      }),
    );
  }

  async setPinned(
    tableName: string,
    columnName: string,
    pinned: boolean,
  ): Promise<void> {
    if (!tableName || !columnName) {
      return;
    }
    const current = await this.get(tableName);
    const index = this.findIndexWithName(current, columnName);
    if (index >= 0) {
      current[index] = { ...current[index], pinned: pinned };
    } else {
      current.push({
        name: columnName.toLowerCase(),
        enabled: true,
        width: null,
        minWidth: null,
        ordinal: current.length,
        pinned: pinned,
      });
    }

    await this.set(tableName, current);
    await this.orderByPinned(tableName, current);
  }

  private settingHasName(setting: ColumnSettings, name: string): boolean {
    return setting.name.toLowerCase() == name.toLowerCase();
  }

  private settingNameInList(
    setting: ColumnSettings,
    columnNameList: string[],
  ): boolean {
    return (
      columnNameList.find(columnName =>
        this.settingHasName(setting, columnName),
      ) != undefined
    );
  }

  async setWidth(
    tableName: string,
    columnName: string,
    width: number,
  ): Promise<void> {
    if (!tableName || !columnName) {
      return;
    }
    const current = await this.get(tableName);
    const index = this.findIndexWithName(current, columnName);
    if (index >= 0) {
      current[index] = { ...current[index], width: width };
    } else {
      current.push({
        name: columnName.toLowerCase(),
        enabled: true,
        width: width,
        minWidth: null,
        ordinal: current.length,
        pinned: false,
      });
    }
    await this.set(tableName, current);
  }

  async setTableHeaderConstants(
    tableName: string,
    columnHeaders: SuperColumn[],
  ) {
    if (!tableName) {
      return;
    }
    const currentSettings = await this.get(tableName);

    currentSettings.forEach(setting => {
      const match = columnHeaders.find(column =>
        this.settingHasName(setting, column.columnTitle),
      );
      setting.minWidth = match ? match.minWidth : setting.minWidth;
    });

    await this.set(tableName, currentSettings);
  }

  async updateTableHeaderList(
    tableName: string,
    columnHeaders: SuperColumn[],
  ): Promise<void> {
    if (!tableName) {
      return;
    }
    let currentSettings = await this.get(tableName);

    currentSettings = currentSettings.filter(setting =>
      columnHeaders.find(c => this.settingHasName(setting, c.columnTitle)),
    );

    for (const column of columnHeaders) {
      const index = this.findIndexWithName(currentSettings, column.columnTitle);
      if (index < 0) {
        currentSettings.push({
          name: column.columnTitle.toLowerCase(),
          enabled: column.optional ? false : true,
          width: null,
          minWidth: null,
          ordinal: currentSettings.length,
          pinned: false,
        });
      }
    }

    await this.set(tableName, currentSettings);
  }
}
